import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";

interface ArticleRatingProps {
  articleId: string;
  organizationId: string;
  initialHelpfulCount?: number;
  initialNotHelpfulCount?: number;
}

export function ArticleRating({ 
  articleId, 
  organizationId, 
  initialHelpfulCount = 0, 
  initialNotHelpfulCount = 0 
}: ArticleRatingProps) {
  const { customer } = useCustomerAuth();
  const { toast } = useToast();
  const [helpfulCount, setHelpfulCount] = useState(initialHelpfulCount);
  const [notHelpfulCount, setNotHelpfulCount] = useState(initialNotHelpfulCount);
  const [userRating, setUserRating] = useState<1 | -1 | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRating = async (rating: 1 | -1) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-article-rating', {
        body: {
          article_id: articleId,
          customer_email: customer?.email || null,
          customer_id: customer?.id || null,
          organization_id: organizationId,
          rating,
          user_agent: navigator.userAgent
        }
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        if (data.error.includes('already rated')) {
          toast({
            title: "Already rated",
            description: "You've already rated this article.",
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error);
      }

      // Update local state with response data
      setUserRating(rating);
      setHelpfulCount(data.helpfulCount);
      setNotHelpfulCount(data.notHelpfulCount);

      toast({
        title: "Thank you!",
        description: "Your feedback helps us improve our help articles.",
      });
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: "Error",
        description: "Failed to submit rating. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 border-t border-border">
      <span className="text-sm text-muted-foreground">Was this helpful?</span>
      
      <div className="flex items-center gap-2">
        <Button
          variant={userRating === 1 ? "default" : "outline"}
          size="sm"
          onClick={() => handleRating(1)}
          disabled={loading || userRating !== null}
          className="gap-2"
        >
          <ThumbsUp className="h-4 w-4" />
          {helpfulCount > 0 && <span>{helpfulCount}</span>}
        </Button>
        
        <Button
          variant={userRating === -1 ? "default" : "outline"}
          size="sm"
          onClick={() => handleRating(-1)}
          disabled={loading || userRating !== null}
          className="gap-2"
        >
          <ThumbsDown className="h-4 w-4" />
          {notHelpfulCount > 0 && <span>{notHelpfulCount}</span>}
        </Button>
      </div>
    </div>
  );
}